export type ElementType = 'text' | 'icon';

export interface BaseElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
}

export interface TextElement extends BaseElement {
  type: 'text';
  content: string;
  fontSize: number;
  color: string;
  fontWeight: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export interface IconElement extends BaseElement {
  type: 'icon';
  iconName: string;
  color: string;
  label?: string;
}

// Map icon - SVG ikona na geografických souřadnicích
export interface MapIcon {
  id: string;
  iconId: string; // ID z map_icons tabulky
  lat: number;
  lng: number;
  width?: number;
  height?: number;
  color?: string;
  label?: string;
  zIndex?: number;
}

export type CanvasElement = IconElement; // Pouze ikony - texty jsou nyní statické

// Text data pro statické textové elementy
export interface PosterTextData {
  title?: string;
  subtitle?: string;
  detail?: string;
}

export type PosterSize = 'B2' | 'A2';
export type Orientation = 'portrait' | 'landscape';
// Frame options aligned with Shopify catalog
export type FrameType = 'none' | 'frame' | 'hanger';
export type FrameColor = 'oak' | 'white' | 'black' | 'teal' | 'teak' | 'walnut' | 'pine';
export type ColorScheme = 'default' | 'monochrome' | 'nautic' | 'blue-white' | 'vintage';
export type MapLayer = 'standard' | 'no-labels' | 'terrain' | 'dark' | 'minimal';
export type WorkspaceBackground = 'gray' | 'living-room' | 'css-shadow' | 'liana' | 'david';
// Presety stylů map
export interface TextElementTemplate {
  id: string;
  content: string;
  x: number; // pozice v procentech (0-100)
  y: number; // pozice v procentech (0-100)
  fontSize: number;
  fontWeight: 'normal' | 'bold' | 'light';
  fontFamily: string;
  color: string;
  textAlign: 'left' | 'center' | 'right';
  type: 'city' | 'country' | 'coordinates' | 'custom';
}

export interface MapStylePreset {
  id: string;
  name: string;
  description: string;
  colorScheme: ColorScheme;
  mapLayer: MapLayer;
  badge?: 'popular' | 'bestseller' | 'new';
  previewUrl?: string;
  textElements?: TextElementTemplate[]; // přednastavené textové prvky
  /**
   * ID posterového layoutu, který se má použít jako výchozí
   * pro textové šablony a dekorace.
   */
  posterStyleId?: string;
}

export const MAP_STYLE_PRESETS: MapStylePreset[] = [
  {
    id: 'modern',
    name: 'Yksinkertainen',
    description: 'Černobílý minimalistický styl',
    colorScheme: 'monochrome',
    mapLayer: 'no-labels',
    badge: 'bestseller',
    posterStyleId: 'minimal',
    textElements: [
      {
        id: 'city-name',
        content: 'STOCKHOLM',
        x: 50,
        y: 85,
        fontSize: 32,
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        color: '#000000',
        textAlign: 'center',
        type: 'city'
      }
    ]
  },
  {
    id: 'asphalt',
    name: 'Tumma',
    description: 'Tmavý městský styl',
    colorScheme: 'default',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal',
    textElements: [
      {
        id: 'city-name',
        content: 'OSLO',
        x: 50,
        y: 82,
        fontSize: 24,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#FFFFFF',
        textAlign: 'center',
        type: 'city'
      }
    ]
  },
  {
    id: 'nautical',
    name: 'Merellinen',
    description: 'Námořní styl s modrými tóny',
    colorScheme: 'nautic',
    mapLayer: 'no-labels',
    badge: 'popular',
    posterStyleId: 'minimal',
    textElements: [
      {
        id: 'city-name',
        content: 'COPENHAGEN',
        x: 50,
        y: 82,
        fontSize: 24,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#2C3E50',
        textAlign: 'center',
        type: 'city'
      }
    ]
  },
  {
    id: 'pantone',
    name: 'Puhdas',
    description: 'Čistý bílý styl',
    colorScheme: 'default',
    mapLayer: 'no-labels',
    posterStyleId: 'paleta'
  },
  {
    id: 'ocean',
    name: 'Valtameri',
    description: 'Oceánský styl',
    colorScheme: 'blue-white',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal',
    textElements: [
      {
        id: 'city-name',
        content: 'PARIS',
        x: 50,
        y: 88,
        fontSize: 36,
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        color: '#1976D2',
        textAlign: 'center',
        type: 'city'
      },
      {
        id: 'country-name',
        content: 'FRANCE',
        x: 50,
        y: 94,
        fontSize: 14,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#1976D2',
        textAlign: 'center',
        type: 'country'
      }
    ]
  },
  {
    id: 'abstract',
    name: 'Abstrakti',
    description: 'Abstraktní umělecký styl',
    colorScheme: 'monochrome',
    mapLayer: 'no-labels',
    badge: 'popular',
    posterStyleId: 'abstrakt',
    textElements: [
      {
        id: 'headline',
        content: 'Paris',
        x: 10,
        y: 10,
        fontSize: 36,
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
        color: '#000000',
        textAlign: 'left',
        type: 'custom'
      },
      {
        id: 'divider',
        content: 'FRANCE',
        x: 90,
        y: 10,
        fontSize: 20,
        fontWeight: 'normal',
        fontFamily: 'Arial, sans-serif',
        color: '#000000',
        textAlign: 'right',
        type: 'custom'
      },
      {
        id: 'tagline',
        content: '48.858°N / 2.351°E',
        x: 10,
        y: 15,
        fontSize: 18,
        fontWeight: 'normal',
        fontFamily: 'Arial, sans-serif',
        color: '#000000',
        textAlign: 'left',
        type: 'coordinates'
      }
    ]
  },
  {
    id: 'near',
    name: 'Lähin',
    description: 'Skandinávský minimalistický styl',
    colorScheme: 'default',
    mapLayer: 'no-labels',
    posterStyleId: 'legenda',
    textElements: [
      {
        id: 'city-name',
        content: 'STOCKHOLM',
        x: 50,
        y: 75,
        fontSize: 20,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#333333',
        textAlign: 'center',
        type: 'city'
      },
      {
        id: 'country-name',
        content: 'SWEDEN',
        x: 50,
        y: 85,
        fontSize: 12,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#666666',
        textAlign: 'center',
        type: 'country'
      }
    ]
  },
  {
    id: 'gaia',
    name: 'Maa',
    description: 'Přírodní zemité tóny',
    colorScheme: 'vintage',
    mapLayer: 'no-labels',
    posterStyleId: 'legenda',
    textElements: [
      {
        id: 'city-name',
        content: 'FUERTEVENTURA',
        x: 50,
        y: 80,
        fontSize: 18,
        fontWeight: 'normal',
        fontFamily: 'Inter, sans-serif',
        color: '#8B4513',
        textAlign: 'center',
        type: 'city'
      }
    ]
  },
  {
    id: 'playroom',
    name: 'Leikkisä',
    description: 'Hravý barevný styl',
    colorScheme: 'default',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal'
  },
  {
    id: 'sky',
    name: 'Taivas',
    description: 'Nebeský styl',
    colorScheme: 'blue-white',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal'
  },
  {
    id: 'humble',
    name: 'Vaatimaton',
    description: 'Skromný styl s texturou',
    colorScheme: 'vintage',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal'
  },
  {
    id: 'custom',
    name: 'Oma',
    description: 'Vlastní nastavení',
    colorScheme: 'default',
    mapLayer: 'no-labels',
    posterStyleId: 'minimal'
  }
];

export const AVAILABLE_FONTS = [
  { name: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Helvetica', value: 'Helvetica, sans-serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Times New Roman', value: '"Times New Roman", serif' },
  { name: 'Courier New', value: '"Courier New", monospace' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { name: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
  { name: 'Impact', value: 'Impact, sans-serif' },
] as const;

export interface PosterDimensions {
  width: number;
  height: number;
}

export const POSTER_SIZES: Record<PosterSize, PosterDimensions> = {
  B2: { width: 500, height: 700 }, // 50x70cm
  A2: { width: 420, height: 594 }, // 42x59.4cm
};

// Definice barev jednotlivých vrstev mapy
export interface LayerColors {
  background: string;
  water: string;
  land: string;
  park: string;
  forest: string;
  building: string;
  building_outline: string;
  road_motorway: string;
  road_major: string;
  road_minor: string;
  road_path: string;
  label_text: string;
  label_halo: string;
}

export type DetailLevel = 'minimal' | 'standard' | 'high' | 'maximum' | 'custom';

// Zoom thresholdy pro jednotlivé vrstvy - může být číslo (min) nebo objekt s min/max
export type LayerZoomThreshold = number | { min?: number; max?: number };

// Custom zoom thresholdy pro jednotlivé vrstvy
export interface LayerZoomThresholds {
  road_motorway?: LayerZoomThreshold;
  road_major_link?: LayerZoomThreshold;
  road_major?: LayerZoomThreshold;
  road_minor_link?: LayerZoomThreshold;
  road_minor?: LayerZoomThreshold;
  road_path?: LayerZoomThreshold;
  road_residential?: LayerZoomThreshold;
  road_pedestrian?: LayerZoomThreshold;
  road_footway_cycleway?: LayerZoomThreshold;
  road_path_track?: LayerZoomThreshold;
  building_outline?: LayerZoomThreshold;
}

export interface MapSettings {
  centerLat: number;
  centerLng: number;
  zoom: number;
  colorScheme: ColorScheme; // Zůstává pro kompatibilitu, ale pro barvy se nepoužívá
  mapLayer: MapLayer;
  showLabels?: boolean;
  showBuildings?: boolean;
  /** ID color presetu z Supabase - univerzální způsob pro barvy map */
  colorPresetId?: string;
  /** Barvy vrstev mapy - buď z presetu nebo přímo nastavené (uloží se jako preset) */
  colors?: LayerColors;
  /** @deprecated Použijte colors místo customColors - zachováno pro kompatibilitu */
  customColors?: LayerColors;
  selectedStyleId?: string; // ID vybraného stylu pro správné označení
  detailLevel?: DetailLevel; // Úroveň detailů mapy
  showMinorRoads?: boolean; // Zobrazit vedlejší silnice a ulice
  showPaths?: boolean; // Zobrazit chodníky, cyklostezky a stezky
  showPedestrian?: boolean; // Zobrazit pěší zóny
  customZoomThresholds?: LayerZoomThresholds; // Vlastní zoom thresholdy pro vrstvy
  /** Násobič detailů mapy - vytvoří větší mapu (více tiles) a CSS transformem ji zmenší na správnou velikost */
  mapDetailMultiplier?: number; // 1 = normální, 2 = 2x větší (více detailů), 3 = 3x větší, atd.
}

// Jednoduchá trasa pro vykreslení na mapě
export interface MapRoute {
  id: string; // unikátní ID trasy pro mapové vrstvy
  // Souřadnice ve formátu [lng, lat]
  coordinates: [number, number][];
  // Volitelné vizuální nastavení trasy
  color?: string; // barva linie
  widthPx?: number; // šířka linie v pixelech
  dashArray?: number[]; // vzor čárkování např. [2, 2]
  opacity?: number; // 0..1
  showStartEnd?: boolean; // vykreslit počáteční a koncový bod
  startMarkerColor?: string; // barva start bodu
  endMarkerColor?: string; // barva koncového bodu
}

export interface MockupPreset {
  name: string;
  offsetX: number;
  offsetY: number;
  scale: number;
  perspective?: number;
  rotateX?: number;
  rotateY?: number;
  rotateZ?: number;
}

export const MOCKUP_PRESETS: Record<string, MockupPreset> = {
  'living-room-center': {
    name: 'Living Room - Center',
    offsetX: 0,
    offsetY: -50,
    scale: 0.4,
    perspective: 1200,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
  },
  'living-room-perspective': {
    name: 'Living Room - Perspective',
    offsetX: 50,
    offsetY: -30,
    scale: 0.38,
    perspective: 1500,
    rotateX: -3,
    rotateY: 5,
    rotateZ: 0,
  },
  'none': {
    name: 'No Preset',
    offsetX: 0,
    offsetY: 0,
    scale: 1,
  },
};

export interface PosterSettings {
  size: PosterSize;
  orientation: Orientation;
  frame: FrameType;
  frameColor?: FrameColor;
  /**
   * ID posterového layoutu (texty, dekorace).
   * Pokud chybí, použije se výchozí hodnota dle mapového presetu.
   */
  styleId?: string;
  offsetX?: number;
  offsetY?: number;
  scale?: number;
  perspective?: number;
  rotateX?: number;
  rotateY?: number;
  rotateZ?: number;
  mockupPreset?: string;
}

// Default barvy byly přesunuty do Supabase color_presets tabulky
// Používejte getColorPresetByScheme() z lib/api.ts pro načítání barev z databáze

